// Enums + the empty form shape for the Fulfill order page.
import { EMPTY_LIST } from '@/lib/utils';
import { rowUnitSymbol } from '@/lib/mappers/quantities';
import { buildBatchesFromValues, FULFILL_UNIT_TYPE_BASIS } from '@/components/common';

export { RETIRE_CREDITS, buildRetireCreditsOptions, FULFILL_UNIT_TYPE } from '@/components/common';

export const SHIPMENT_OPTION = Object.freeze({
  createNew: 'CREATE_NEW',
  selectExisting: 'SELECT_EXISTING',
  addLater: 'ADD_LATER',
});

// Every field the form owns; a missing key here would make its input uncontrolled.
export const EMPTY_FULFILL_ORDER_FORM = Object.freeze({
  facilityId: '',
  terminalId: '',
  retireCredits: '',
  shipmentOption: '',
  // The server's own auto-generated shipment numbers use this prefix.
  idPrefix: 'SP',
  idNumber: '',
  shipmentQuantity: '',
  shipmentQuantityUnit: '',
  dateOfDispatch: '',
  vesselName: '',
  transportName: '',
  // "Select existing outgoing shipment" — a different value than idNumber above, which is Create new's own typed id.
  existingShipmentId: '',
  // Written by <SelectFromTable> via setValue, never a rendered control of its own.
  selectedInbounds: EMPTY_LIST,
  fulfillUnitType: '',
  hasMixedUnitBatches: false,
});

const totalCertified = (values) =>
  buildBatchesFromValues(values).reduce(
    (sum, batch) => sum + (Number(batch.certifiedQuantity) || 0),
    0,
  );

// Create new assigns the shipment's size; Select existing what this fulfilment certifies (the server keeps the larger of that and any prior assignment).
export const resolveAssignedQuantity = (values = {}) => {
  if (values.shipmentOption === SHIPMENT_OPTION.createNew) return values.shipmentQuantity;
  if (values.shipmentOption === SHIPMENT_OPTION.selectExisting) {
    const total = totalCertified(values);
    return total > 0 ? String(total) : '';
  }
  return '';
};

// The unit the assigned quantity travels in: the typed shipment unit, or the basis the picks were read in.
export const resolveAssignedQuantityUnit = (values = {}, order) =>
  values.shipmentOption === SHIPMENT_OPTION.createNew
    ? values.shipmentQuantityUnit
    : rowUnitSymbol(order, FULFILL_UNIT_TYPE_BASIS[values.fulfillUnitType]);

// Cleared the moment Shipment option changes again — a stale value from one path must not leak into the other.
export const SHIPMENT_OPTION_CLEARS = Object.freeze([
  'idNumber',
  'shipmentQuantity',
  'shipmentQuantityUnit',
  'dateOfDispatch',
  'vesselName',
  'transportName',
  'existingShipmentId',
]);

// The three ways an order's shipment can be settled — the radio's own options.
export const buildShipmentOptionOptions = (t) => [
  { value: SHIPMENT_OPTION.createNew, label: t('ORDERS_FULFILL_SHIPMENT_OPTION_CREATE_NEW') },
  {
    value: SHIPMENT_OPTION.selectExisting,
    label: t('ORDERS_FULFILL_SHIPMENT_OPTION_SELECT_EXISTING'),
  },
  { value: SHIPMENT_OPTION.addLater, label: t('ORDERS_FULFILL_SHIPMENT_OPTION_ADD_LATER') },
];

// The units the order can be shipped in, deduped — a material may use one for both bases.
export const buildShipmentUnitOptions = (t, workingUnit, declarationUnit) => {
  const seen = new Set();
  return [
    [workingUnit, t('UNIT_WORKING')],
    [declarationUnit, t('UNIT_DECLARATION')],
  ]
    .filter(([unit]) => unit && !seen.has(unit) && seen.add(unit))
    .map(([unit, badgeText]) => ({ value: unit, label: unit, badgeText }));
};
